import React from 'react';
import { Check } from 'lucide-react';

const BULLETS = [
  'Set a target cash-out value in any currency',
  'Track realized proceeds from your logged sells',
  'Layer in planned sells to project your progress',
];

const GREEN = '#6db981';
// Green diagonal stripes for the "planned" segment, mirrors the real app card.
const STRIPES =
  'repeating-linear-gradient(45deg, rgba(109,185,129,0.6) 0px, rgba(109,185,129,0.6) 5px, rgba(109,185,129,0.26) 5px, rgba(109,185,129,0.26) 10px)';

export function CashOutGoalSection() {
  return (
    <section
      className="px-6 py-24 relative"
      style={{
        borderTop: '0.5px solid rgba(255,255,255,0.06)',
        background:
          'linear-gradient(180deg, rgba(109,185,129,0.04), transparent 60%)',
      }}
    >
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-12 items-center">
        {/* LEFT, 2/5 - copy */}
        <div className="md:col-span-2">
          <div
            className="text-[11px] font-semibold tracking-[0.18em] mb-4"
            style={{ color: '#d4b483' }}
          >
            GOAL TRACKING
          </div>
          <h2
            className="font-semibold"
            style={{
              color: '#f4f1ea',
              fontSize: 'clamp(26px, 4vw, 36px)',
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
            }}
          >
            Set a cash-out goal.
          </h2>
          <p
            className="mt-4 text-[15px] leading-relaxed"
            style={{ color: 'rgba(244,241,234,0.6)' }}
          >
            Pick a target value and watch your progress toward it, from the real
            money you&apos;ve already cashed out to the sells you&apos;re still
            planning.
          </p>

          <ul className="mt-6 space-y-3">
            {BULLETS.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <span
                  className="flex items-center justify-center rounded-full mt-0.5"
                  style={{
                    width: 18,
                    height: 18,
                    backgroundColor: 'rgba(212,180,131,0.15)',
                  }}
                >
                  <Check className="w-3 h-3" style={{ color: '#d4b483' }} strokeWidth={3} />
                </span>
                <span className="text-[14px]" style={{ color: 'rgba(244,241,234,0.8)' }}>
                  {b}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* RIGHT, 3/5 - static mock card */}
        <div className="md:col-span-3">
          <div
            className="rounded-xl"
            style={{
              backgroundColor: '#131313',
              border: '0.5px solid rgba(255,255,255,0.08)',
              padding: '18px',
            }}
          >
            <div
              className="text-[10px] tracking-[0.14em] mb-1"
              style={{ color: 'rgba(244,241,234,0.45)' }}
            >
              CASH-OUT GOAL
            </div>
            <div
              className="font-bold tabular-nums leading-tight"
              style={{ color: '#f4f1ea', fontSize: 26 }}
            >
              $100,000
            </div>
            <div className="text-[12px] mt-0.5" style={{ color: 'rgba(244,241,234,0.55)' }}>
              68% covered by realized + planned
            </div>

            {/* Bar top */}
            <div className="flex items-center justify-between gap-2 mt-4 mb-1.5 flex-wrap">
              <span className="text-[12px] font-medium" style={{ color: '#f4f1ea' }}>
                68% projected
              </span>
              <span className="text-[12px] tabular-nums" style={{ color: 'rgba(244,241,234,0.55)' }}>
                $68,000 of $100,000
              </span>
            </div>

            {/* Progress bar: realized (green) + planned (striped) + remaining track */}
            <div
              className="flex overflow-hidden"
              style={{ height: 12, borderRadius: 9999, background: 'rgba(255,255,255,0.06)' }}
            >
              <div style={{ width: '58%', background: GREEN }} />
              <div style={{ width: '10%', backgroundImage: STRIPES }} />
            </div>

            {/* Legend */}
            <div className="flex items-center gap-x-4 gap-y-1.5 mt-3 flex-wrap text-[11px]">
              <span className="inline-flex items-center gap-1.5" style={{ color: 'rgba(244,241,234,0.55)' }}>
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: GREEN }} />
                Realized <span className="tabular-nums" style={{ color: '#f4f1ea' }}>$58,000</span>
              </span>
              <span className="inline-flex items-center gap-1.5" style={{ color: 'rgba(244,241,234,0.55)' }}>
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundImage: STRIPES }} />
                Planned <span className="tabular-nums" style={{ color: '#f4f1ea' }}>$10,000</span>
              </span>
              <span className="inline-flex items-center gap-1.5" style={{ color: 'rgba(244,241,234,0.55)' }}>
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: 'rgba(255,255,255,0.12)' }} />
                Remaining <span className="tabular-nums" style={{ color: '#f4f1ea' }}>$32,000</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
